import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import {
  Auth,
  GoogleAuthProvider,
  isSignInWithEmailLink,
  onAuthStateChanged,
  signInWithEmailLink,
  signInWithPopup,
  signOut,
} from '@angular/fire/auth';
import { User } from '@firebase/auth';
import { LoggerService } from '@dibsondance/shared-logging';

import { AuthService } from './authentication.service';

@Injectable()
export class FirebaseAuthenticationService implements AuthService {
  protected readonly auth = inject(Auth);
  private readonly logger = inject(LoggerService);

  private _user = signal<User | null>(null);
  private _idToken = signal<string | null>(null);

  user = this._user.asReadonly();
  idToken = this._idToken.asReadonly();

  constructor() {
    this.initializeAuthStateListener();
  }

  private initializeAuthStateListener(): void {
    onAuthStateChanged(this.auth, async (user) => {
      this.logger.info(
        'Auth state changed',
        { status: user ? 'User logged in' : 'User logged out' },
        'AUTH-BASE'
      );

      if (user) {
        // User is signed in, get the ID token
        try {
          const idToken = await user.getIdToken();
          this._user.set(user);
          this._idToken.set(idToken);
          this.logger.debug(
            'User state restored from Firebase persistence',
            undefined,
            'AUTH-BASE'
          );
        } catch {
          this._user.set(user);
          this._idToken.set(null);
        }
      } else {
        // User is signed out
        this._user.set(null);
        this._idToken.set(null);
      }
    });
  }

  async completeMagicLinkSignIn(emailLink: string): Promise<boolean> {
    try {
      if (!isSignInWithEmailLink(this.auth, emailLink)) {
        this.logger.error('Invalid Firebase magic link', undefined, undefined, 'AUTH-BASE');
        return false;
      }

      const email = localStorage.getItem('emailForSignIn');
      if (!email) {
        this.logger.error('No email found in localStorage for magic link', undefined, undefined, 'AUTH-BASE');
        return false;
      }

      await signInWithEmailLink(this.auth, email, emailLink);
      localStorage.removeItem('emailForSignIn');

      this.logger.info('Magic link sign-in completed', undefined, 'AUTH-BASE');
      // User state updated automatically via onAuthStateChanged
      return true;
    } catch (error) {
      this.logger.error('Magic link completion failed', error as Error, undefined, 'AUTH-BASE');
      return false;
    }
  }

  async signInWithGoogle(): Promise<boolean> {
    try {
      this.logger.debug(
        'Starting Google sign-in process',
        undefined,
        'AUTH-BASE'
      );

      // Create Google Auth provider
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');

      const authOperation = signInWithPopup(this.auth, provider);
      const result = await FirebaseAuthenticationService.createTimeoutPromise(
        authOperation,
        30000
      );

      const user = result.user;
      if (!user) {
        return false;
      }

      this.logger.info(
        'Firebase authentication successful',
        undefined,
        'AUTH-BASE'
      );

      // Note: User state will be automatically updated via onAuthStateChanged listener
      // Note: Session cookie creation should be handled by the host-level service
      // This base service only handles Firebase authentication
      return true;
    } catch {
      return false;
    }
  }

  // Override in host/portal services — base has no HTTP client for the API call
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  signInWithMagicLink(_email: string): Promise<boolean> {
    return Promise.resolve(false);
  }

  async signOut(): Promise<boolean> {
    try {
      await signOut(this.auth);
      this._user.set(null);
      this._idToken.set(null);
      return true;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_) {
      return false;
    }
  }

  isLoggedIn(): Signal<boolean> {
    return computed(() => {
      // Check Firebase Auth's current user directly for most up-to-date state
      const firebaseUser = this.auth.currentUser;
      const signalUser = this.user();

      this.logger.debug(
        'isLoggedIn check',
        {
          firebaseUser: !!firebaseUser,
          signalUser: !!signalUser,
          firebaseEmail: firebaseUser?.email,
        },
        'AUTH-BASE'
      );

      // Use Firebase's currentUser as the source of truth
      return !!firebaseUser;
    });
  }

  private static createTimeoutPromise<T>(
    operation: Promise<T>,
    timeoutMs: number
  ): Promise<T> {
    return Promise.race([
      operation,
      new Promise<T>((_, reject) => {
        setTimeout(() => {
          reject(`Operation timed out after ${timeoutMs}ms`);
        }, timeoutMs);
      }),
    ]);
  }
}
